import { EdiCreatedDateFilter } from '../edi-created-date-filter';
import { Injectable } from '@angular/core';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { EdiDespatchAdvice } from '../../models/edi';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';
import { EdiService } from '../edi/edi.service';
import { format } from 'date-fns';
import { gungComparatorHelper } from 'gung-common';
import { EdiMinDateConfigService } from '../edi-min-date-config.service';
import {
  BuyerEdiOrderFilter,
  ConfigIdFilter,
  DeliveryEdiOrderFilter,
  InvoiceStatusFilter,
  SupplierEdiOrderFilter,
  ValidationFilter
} from '../edi-order-list-config/edi-order-list-config.service';

export class EdiDispatchDateFilter extends SimpleConfigBaseFilter<EdiDespatchAdvice> {
  constructor() {
    super();
  }
  getName(): string {
    return 'DESPATCH_ADVICE_DATE';
  }
  getOptionIds(item: EdiDespatchAdvice): string[] {
    let date = item.deliveryDate ? new Date(item.deliveryDate) : new Date();
    return [format(date, 'yyyy-MM-dd')];
  }
  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EdiDispatchAdvicesListConfigService implements EdiMinDateConfigService<EdiDespatchAdvice> {
  protected minDateSubject = new BehaviorSubject<string>(this.getDefaultMinDate());
  protected glnNameMap: Record<string, string> = {};

  constructor(protected ediService: EdiService, protected baseViewConfigService: BaseViewConfigService) {
    this.ediService.getGlnNumbers().subscribe(glnNumbers => {
      glnNumbers.forEach(entry => {
        this.glnNameMap[String(entry.gln)] = `${entry.gln} - ${entry.b24.name} - ${entry.companyId}`;
      });
    });
  }

  getDefaultMinDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return format(date, 'yyyy-MM-dd');
  }

  setMinDate(minDate: string): void {
    this.minDateSubject.next(minDate);
  }

  getMinDate(): string {
    return this.minDateSubject.getValue();
  }

  getItems(): Observable<EdiDespatchAdvice[]> {
    return this.minDateSubject.pipe(
      switchMap(minDate => this.ediService.getDispatchAdvices(minDate)),
      map(items => items.sort((a, b) => gungComparatorHelper([a.timestamp, a.id], [b.timestamp, b.id], -1)))
    );
  }

  getFilters(): ConfigBaseFilter<EdiDespatchAdvice>[] {
    return [
      new EdiCreatedDateFilter(),
      new ValidationFilter(),
      new InvoiceStatusFilter(),
      new ConfigIdFilter(),
      new EdiDispatchDateFilter(),
      new SupplierEdiOrderFilter(this.glnNameMap),
      new DeliveryEdiOrderFilter(this.glnNameMap),
      new BuyerEdiOrderFilter(this.glnNameMap)
    ];
  }

  getSortOptions(): ListSortOption<EdiDespatchAdvice>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<EdiDespatchAdvice>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => this.baseViewConfigService.getEdiDispatchCardListComponent(),
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'EDI despatch advices grid'
      }
    ];
  }

  getSearchTerms(item: EdiDespatchAdvice): string[] {
    const generalSearchTerms = [
      item.documentReferences?.supplierOrder?.id,
      item.documentReferences?.buyerOrder?.id,
      item.documentReferences?.invoice?.id,
      item.documentReferences?.invoiceOcr?.id,
      item.documentReferences?.despatchAdvice?.id,
      item.documentReferences?.parcel?.id
    ].filter(s => !!s);
    return [item.id, item.consigneeGln?.toString(), item.buyerGln?.toString()].concat(generalSearchTerms);
  }

  getItemId(item: EdiDespatchAdvice): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
